import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import {
  getCampaignBoundaries,
  isCampaignActiveAt,
  isTriggerUnlockedAt,
} from './campaign';
import { getMostraSeuAprovaState } from './queries';

const QUERY_KEY = ['mostraSeuAprova', 'state'];

/**
 * `setTimeout` estoura acima de ~24,8 dias (int32). Reagenda em fatias de até
 * 6h; a cada disparo a elegibilidade é reavaliada e um novo timer é armado.
 */
const MAX_TIMEOUT_MS = 6 * 60 * 60 * 1000;

/**
 * Relógio que acorda nas viradas que mudam a elegibilidade — fim da campanha e
 * liberação do próximo slot.
 *
 * A resposta do estado é cacheada e nada a refaz numa aba que fica aberta
 * (`refetchOnWindowFocus` desligado, sem `refetchInterval`). Sem isto, um modal
 * seguiria elegível depois das 23:59 do dia 16, ou um slot liberado no meio da
 * madrugada só apareceria no próximo reload.
 *
 * `boundaries` precisa ser estável entre renders (memoize na origem), senão o
 * timer é rearmado a cada render.
 */
function useCampaignClock(boundaries: number[]): number {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    // Só limites estritamente futuros entram aqui, então cada disparo avança
    // `nowMs` para além de um limite — não há laço de re-render.
    const next = boundaries
      .filter((time) => time > nowMs)
      .sort((a, b) => a - b)[0];

    if (next === undefined) return undefined;

    // Meio segundo de folga para não acordar exatamente na virada e reagendar
    // por arredondamento.
    const delay = Math.min(Math.max(next - Date.now(), 0) + 500, MAX_TIMEOUT_MS);

    const timer = window.setTimeout(() => setNowMs(Date.now()), delay);

    return () => window.clearTimeout(timer);
  }, [boundaries, nowMs]);

  return nowMs;
}

/**
 * Estado da campanha para o aluno logado, já revalidado no tempo.
 *
 * `canShowPresentation` e `canFireTrigger` são pré-condições para nem chamar o
 * servidor à toa — a decisão final continua sendo dele, no `POST /view`.
 */
function useCampaignState() {
  const { user } = useUserStore();

  const { data, isPending, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getMostraSeuAprovaState,
    enabled: !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    // A campanha dura semanas e o estado só muda por ação do próprio aluno (ou
    // pelo envio do vídeo, que tem cache curto no servidor).
    staleTime: 5 * 60 * 1000,
  });

  const boundaries = useMemo(() => getCampaignBoundaries(data), [data]);
  const nowMs = useCampaignClock(boundaries);

  const isActive = isCampaignActiveAt(data?.campaign, nowMs);

  return {
    state: data,
    isLoading: isPending,
    isError,
    isActive,
    canShowPresentation: isActive && !!data?.presentation.eligible,
    canFireTrigger:
      isActive && !!data?.triggers.eligibleNow && isTriggerUnlockedAt(data, nowMs),
  };
}

export { QUERY_KEY, useCampaignClock, useCampaignState };
